import EastIcon from "@mui/icons-material/East";
import WestIcon from "@mui/icons-material/West";
import {
  Autocomplete,
  Box,
  FormGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useCallback,
  useMemo,
  useState,
  type FunctionComponent,
  type FocusEvent,
  type MouseEvent,
  type SyntheticEvent,
} from "react";
import Katex from "../latex/Katex";

interface GraphToolbarProps {
  isReverseMode: boolean;
  derivativeTarget: string | null;
  nodeIds: string[];
  nodeNames: string[];
  onReverseModeChange: (isReversedMode: boolean) => void;
  onDerivativeTargetChange: (nodeId: string | null) => void;
}

interface AutocompleteOption {
  // Plain text, used for filtering, for the editable input and for a11y
  label: string;
  // The node name to typeset, e.g. `a_1` renders as a with a subscript 1
  latex: string;
  // ` (id)` when two nodes share a name, empty otherwise
  suffix: string;
  id: string;
}

const GraphToolbar: FunctionComponent<GraphToolbarProps> = ({
  isReverseMode,
  derivativeTarget,
  nodeIds,
  nodeNames,
  onReverseModeChange,
  onDerivativeTargetChange,
}) => {
  const options: AutocompleteOption[] = useMemo(() => {
    const seenNodeNames = new Set<string>();
    return nodeIds.map((nodeId, index) => {
      const nodeName = nodeNames[index];
      const suffix = seenNodeNames.has(nodeName) ? ` (${nodeId})` : "";

      seenNodeNames.add(nodeName);
      return {
        label: `${nodeName}${suffix}`,
        latex: nodeName,
        suffix,
        id: nodeId,
      };
    });
  }, [nodeIds, nodeNames]);

  const selectedValue: AutocompleteOption | null = useMemo(() => {
    const option = options.find((option) => option.id === derivativeTarget);
    if (option === undefined) {
      return null;
    }
    return option;
  }, [derivativeTarget, options]);

  // While the field is idle the name is shown typeset; focusing it hands the
  // raw text back so the user can type to search
  const [isTargetFocused, setTargetFocused] = useState(false);
  const isShowingMath = !isTargetFocused && selectedValue !== null;

  const handleTargetFocus = useCallback(() => {
    setTargetFocused(true);
  }, []);

  const handleTargetBlur = useCallback(() => {
    setTargetFocused(false);
  }, []);

  /**
   * Tracks focus on the input itself.
   *
   * The Autocomplete root never sees these: MUI attaches its own focus
   * handlers to the input, so ours have to sit there too and hand the event on
   * to MUI's, which is what opens and closes the dropdown.
   */
  const buildTargetFocusProps = useCallback(
    (inputProps: Record<string, any>): Record<string, any> => {
      return {
        ...inputProps,
        onFocus: (event: FocusEvent<HTMLInputElement>) => {
          inputProps.onFocus?.(event);
          handleTargetFocus();
        },
        onBlur: (event: FocusEvent<HTMLInputElement>) => {
          inputProps.onBlur?.(event);
          handleTargetBlur();
        },
      };
    },
    [handleTargetBlur, handleTargetFocus],
  );

  const handleReverseModeChange = useCallback(
    (event: MouseEvent<HTMLElement>, newMode: string | null) => {
      // ToggleButtonGroup reports null when clicking the already-selected
      // button, ignoring it keeps one mode always selected
      if (newMode === null) {
        return;
      }
      onReverseModeChange(newMode === "reverse");
    },
    [onReverseModeChange],
  );

  const handleDerivativeTargetChange = useCallback(
    (event: SyntheticEvent, newValue: AutocompleteOption | null) => {
      if (newValue === null) {
        onDerivativeTargetChange(null);
      } else {
        onDerivativeTargetChange(newValue.id);
      }
    },
    [onDerivativeTargetChange],
  );

  return (
    <Box display="flex" alignItems="center" gap={2}>
      {/* Differentiation mode */}
      <FormGroup>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={isReverseMode ? "reverse" : "forward"}
          onChange={handleReverseModeChange}
          aria-label="differentiation-mode"
          sx={{
            "& .MuiToggleButton-root": {
              paddingY: 0.25,
              paddingX: 1.25,
              fontSize: 13,
              color: "text.secondary",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              color: "primary.main",
              backgroundColor: "action.selected",
            },
          }}
        >
          {/* The tooltips must live inside the buttons: ToggleButtonGroup
              injects `selected` and `onChange` into its direct children, so
              wrapping a button in a Tooltip would swallow them */}
          <ToggleButton
            value="forward"
            aria-label="forward-mode-differentiation"
          >
            <Tooltip title="Forward-mode differentiation: propagates d(node)/d(target) from the derivative target toward the outputs">
              <Box component="span" display="flex" alignItems="center">
                <EastIcon fontSize="small" sx={{ mr: 0.5 }} />
                Forward
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="reverse"
            aria-label="reverse-mode-differentiation"
          >
            <Tooltip title="Reverse-mode differentiation (backpropagation): propagates d(target)/d(node) from the derivative target toward the inputs">
              <Box component="span" display="flex" alignItems="center">
                <WestIcon fontSize="small" sx={{ mr: 0.5 }} />
                Reverse
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </FormGroup>

      {/* Derivative target */}
      <FormGroup>
        <Autocomplete
          data-testid="derivative-target"
          options={options}
          value={selectedValue}
          sx={{ width: 220 }}
          size="small"
          onChange={handleDerivativeTargetChange}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Katex latex={option.latex} />
              {option.suffix !== "" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  {option.suffix}
                </Typography>
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Derivative Target"
              InputProps={{
                ...params.InputProps,
                startAdornment:
                  isShowingMath && selectedValue !== null ? (
                    <Box
                      data-testid="derivative-target-math"
                      display="flex"
                      alignItems="center"
                      pl={0.5}
                    >
                      <Katex latex={selectedValue.latex} />
                      {selectedValue.suffix !== "" && (
                        <Typography variant="body2" color="text.secondary">
                          {selectedValue.suffix}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    params.InputProps.startAdornment
                  ),
              }}
              inputProps={buildTargetFocusProps(params.inputProps)}
              // The raw name stays in the input so filtering, keyboard
              // selection and screen readers keep working; it is only hidden
              // while the typeset name stands in for it
              sx={
                isShowingMath
                  ? { "& input": { color: "transparent" } }
                  : undefined
              }
            />
          )}
        />
      </FormGroup>
    </Box>
  );
};

export default GraphToolbar;
